export interface CanvasFitLayout {
  /** アスペクト比 (width / height) */
  aspectRatio: number;
  /** キャンバス上の描画ピクセル幅 */
  renderWidth: number;
  /** キャンバス上の描画ピクセル高さ */
  renderHeight: number;
  /** キャンバス左上からの描画開始Xオフセット */
  offsetX: number;
  /** キャンバス左上からの描画開始Yオフセット */
  offsetY: number;
  /** 元画像からのスケーリング倍率 */
  scale: number;
  /** キャンバス全体に対する正規化矩形領域 [0.0, 1.0] */
  normalizedBounds: {
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
  };
}

export interface UVCoordResult {
  u: number;
  v: number;
  isInside: boolean;
}

export interface CanvasCoordResult {
  canvasX: number;
  canvasY: number;
}

/**
 * アスペクト比計算＆キャンバスフィッティングコンポーネント (AspectRatioFittingComponent)
 * 元画像の歪みゼロのアスペクト比維持、キャンバスサイズに対する contain フィッティング幾何計算、
 * およびスクリーンピクセル座標と正規化UV座標の相互マッピングを提供する。
 */
export class AspectRatioFitter {
  /**
   * 画像のアスペクト比（幅 / 高さ）を算出
   */
  public calculateAspectRatio(width: number, height: number): number {
    if (!width || !height || width <= 0 || height <= 0) {
      return 1.0;
    }
    return width / height;
  }

  /**
   * キャンバス表示領域に対する contain フィッティングレイアウトを計算
   * @param imageWidth 元画像のピクセル幅
   * @param imageHeight 元画像のピクセル高さ
   * @param canvasWidth 描画先キャンバスの表示幅
   * @param canvasHeight 描画先キャンバスの表示高さ
   * @param paddingRatio キャンバス内安全マージン比率 (既定: 0.90 = 90%領域に内接)
   */
  public calculateFit(
    imageWidth: number,
    imageHeight: number,
    canvasWidth: number,
    canvasHeight: number,
    paddingRatio: number = 0.90
  ): CanvasFitLayout {
    // 異常値保護
    if (imageWidth <= 0 || imageHeight <= 0 || canvasWidth <= 0 || canvasHeight <= 0) {
      return {
        aspectRatio: 1.0,
        renderWidth: Math.max(1, canvasWidth),
        renderHeight: Math.max(1, canvasHeight),
        offsetX: 0,
        offsetY: 0,
        scale: 1.0,
        normalizedBounds: { xMin: 0, yMin: 0, xMax: 1, yMax: 1 },
      };
    }

    const clampedPadding = Math.max(0.1, Math.min(1.0, paddingRatio));
    const aspectRatio = imageWidth / imageHeight;

    const availWidth = canvasWidth * clampedPadding;
    const availHeight = canvasHeight * clampedPadding;

    // 縦横等倍スケーリング: S = min(availWidth / imageWidth, availHeight / imageHeight)
    const scale = Math.min(availWidth / imageWidth, availHeight / imageHeight);

    const renderWidth = imageWidth * scale;
    const renderHeight = imageHeight * scale;

    // センタリングオフセット
    const offsetX = (canvasWidth - renderWidth) / 2;
    const offsetY = (canvasHeight - renderHeight) / 2;

    // 正規化矩形領域 [0.0, 1.0]
    const xMin = offsetX / canvasWidth;
    const yMin = offsetY / canvasHeight;
    const xMax = (offsetX + renderWidth) / canvasWidth;
    const yMax = (offsetY + renderHeight) / canvasHeight;

    return {
      aspectRatio,
      renderWidth,
      renderHeight,
      offsetX,
      offsetY,
      scale,
      normalizedBounds: {
        xMin,
        yMin,
        xMax,
        yMax,
      },
    };
  }

  /**
   * キャンバス座標 (canvasX, canvasY) を正規化テクスチャUV座標 [0.0, 1.0] へ変換
   */
  public canvasToImageUV(canvasX: number, canvasY: number, layout: CanvasFitLayout): UVCoordResult {
    if (layout.renderWidth <= 0 || layout.renderHeight <= 0) {
      return { u: 0, v: 0, isInside: false };
    }

    const u = (canvasX - layout.offsetX) / layout.renderWidth;
    const v = (canvasY - layout.offsetY) / layout.renderHeight;

    const isInside = u >= 0.0 && u <= 1.0 && v >= 0.0 && v <= 1.0;

    return { u, v, isInside };
  }

  /**
   * 正規化テクスチャUV座標 [0.0, 1.0] をキャンバス座標へ変換
   */
  public imageUVToCanvas(u: number, v: number, layout: CanvasFitLayout): CanvasCoordResult {
    const canvasX = layout.offsetX + u * layout.renderWidth;
    const canvasY = layout.offsetY + v * layout.renderHeight;

    return { canvasX, canvasY };
  }
}
